import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Users, CheckCircle, XCircle, AlertTriangle, Eye, Power, Clock, DollarSign, RefreshCw, Building, ArrowUpDown, ClipboardCheck, CheckSquare, Square } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// QA Checklist Items (must match backend)
const QA_CHECKLIST_ITEMS = [
  { item: "call_connects", label: "Call connects successfully" },
  { item: "voice_quality", label: "Voice quality acceptable (no robotic pauses)" },
  { item: "correct_greeting", label: "Correct greeting & business name" },
  { item: "intent_handling", label: "At least one intent handled correctly" },
  { item: "lead_or_booking", label: "Lead captured OR booking flow triggered" },
  { item: "recording_received", label: "Recording + summary received in dashboard" }
];

export function AdminClientsPage() {
  const { adminKey } = useOutletContext();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [actionReason, setActionReason] = useState("");
  const [graceHours, setGraceHours] = useState(72);
  const [filter, setFilter] = useState({ status: "", billing: "" });
  const [newPlan, setNewPlan] = useState("");
  
  // QA Modal State
  const [qaModal, setQaModal] = useState(false);
  const [qaStatus, setQaStatus] = useState(null);
  const [qaChecklist, setQaChecklist] = useState(
    QA_CHECKLIST_ITEMS.map(item => ({ ...item, passed: false, notes: "" }))
  );
  const [qaReason, setQaReason] = useState("");
  
  // QA Proof Fields (REQUIRED)
  const [qaTestCallId, setQaTestCallId] = useState("");
  const [qaRecordingUrl, setQaRecordingUrl] = useState("");
  const [qaSummaryReceived, setQaSummaryReceived] = useState(false);
  const [qaPerformedBy, setQaPerformedBy] = useState("");

  useEffect(() => {
    fetchClients();
  }, [adminKey, filter]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/admin/clients?limit=100`;
      if (filter.status) url += `&status=${filter.status}`;
      if (filter.billing) url += `&billing_status=${filter.billing}`;
      
      const res = await fetch(url, {
        headers: { "X-Admin-Key": adminKey }
      });
      
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients || []);
      }
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientDetail = async (clientId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${clientId}`, {
        headers: { "X-Admin-Key": adminKey }
      });
      
      if (res.ok) {
        setClientDetail(await res.json());
      }
      
      // Also fetch QA status
      fetchQaStatus(clientId);
    } catch (err) {
      console.error("Failed to fetch client detail:", err);
    }
  };

  const fetchQaStatus = async (clientId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${clientId}/qa-status`, {
        headers: { "X-Admin-Key": adminKey }
      });
      
      if (res.ok) {
        const data = await res.json();
        setQaStatus(data);
        
        // Pre-fill checklist if QA already done
        if (data.qa_checklist && data.qa_checklist.length > 0) {
          setQaChecklist(QA_CHECKLIST_ITEMS.map(item => {
            const existing = data.qa_checklist.find(c => c.item === item.item);
            return existing || { ...item, passed: false, notes: "" };
          }));
        } else {
          setQaChecklist(QA_CHECKLIST_ITEMS.map(item => ({ ...item, passed: false, notes: "" })));
        }
      }
    } catch (err) {
      console.error("Failed to fetch QA status:", err);
    }
  };

  const handleQaPass = async () => {
    // Validate required proof fields
    if (!qaTestCallId.trim()) {
      alert("Test Call ID is required (proof of test call)");
      return;
    }
    if (!qaSummaryReceived) {
      alert("You must confirm the summary was received in dashboard");
      return;
    }
    if (!qaPerformedBy.trim()) {
      alert("Performed By (your name) is required for audit");
      return;
    }
    if (!qaReason.trim()) {
      alert("Reason/Notes is required");
      return;
    }
    
    const allPassed = qaChecklist.every(item => item.passed);
    if (!allPassed) {
      alert("All checklist items must be passed");
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${selectedClient}/qa-pass`, {
        method: "POST",
        headers: {
          "X-Admin-Key": adminKey,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          test_call_id: qaTestCallId.trim(),
          recording_url: qaRecordingUrl.trim() || null,
          summary_received: qaSummaryReceived,
          checklist: qaChecklist,
          reason: qaReason.trim(),
          performed_by: qaPerformedBy.trim()
        })
      });
      
      if (res.ok) {
        alert("QA marked as PASSED with verified proof. Client can now be activated.");
        setQaModal(false);
        resetQaForm();
        fetchClientDetail(selectedClient);
      } else {
        const err = await res.json();
        const errorMsg = err.detail?.message || err.detail?.error || JSON.stringify(err.detail);
        alert(`Error: ${errorMsg}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const resetQaForm = () => {
    setQaReason("");
    setQaTestCallId("");
    setQaRecordingUrl("");
    setQaSummaryReceived(false);
    setQaPerformedBy("");
    setQaChecklist(QA_CHECKLIST_ITEMS.map(item => ({ ...item, passed: false, notes: "" })));
  };

  const handleQaFail = async () => {
    if (!qaReason.trim()) {
      alert("Reason is required");
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${selectedClient}/qa-fail`, {
        method: "POST",
        headers: {
          "X-Admin-Key": adminKey,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ reason: qaReason })
      });
      
      if (res.ok) {
        alert("QA marked as FAILED. Client cannot be activated.");
        setQaModal(false);
        setQaReason("");
        fetchClientDetail(selectedClient);
      } else {
        const err = await res.json();
        alert(`Error: ${err.detail}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const toggleQaItem = (index) => {
    setQaChecklist(prev => prev.map((item, i) => 
      i === index ? { ...item, passed: !item.passed } : item
    ));
  };

  const handleAction = async (action, clientId) => {
    if (!actionReason.trim()) {
      alert("Reason is required for audit trail");
      return;
    }

    try {
      let endpoint = "";
      let body = { reason: actionReason };

      switch (action) {
        case "activate":
          endpoint = `/api/admin/clients/${clientId}/activate`;
          break;
        case "suspend":
          endpoint = `/api/admin/clients/${clientId}/suspend`;
          break;
        case "grant-grace":
          endpoint = `/api/admin/clients/${clientId}/grant-grace`;
          body.hours = graceHours;
          break;
        case "waive-setup":
          endpoint = `/api/admin/clients/${clientId}/waive-setup-fee`;
          break;
        case "change-plan":
          endpoint = `/api/billing/admin/client/${clientId}/plan`;
          body.plan_id = newPlan;
          break;
        default:
          return;
      }

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "X-Admin-Key": adminKey,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        setActionModal(null);
        setActionReason("");
        fetchClients();
        if (selectedClient) fetchClientDetail(selectedClient);
        alert(`Action completed successfully`);
      } else {
        const err = await res.json();
        alert(`Error: ${err.detail || "Failed to perform action"}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Client Management</h1>
        <button 
          onClick={fetchClients}
          className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-800 rounded-lg hover:bg-gray-200"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <select 
          value={filter.status}
          onChange={(e) => setFilter(f => ({ ...f, status: e.target.value }))}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-800"
        >
          <option value="">All Status</option>
          <option value="live">Live</option>
          <option value="in_review">In Review</option>
          <option value="pending_setup">Pending Setup</option>
          <option value="suspended">Suspended</option>
        </select>
        <select 
          value={filter.billing}
          onChange={(e) => setFilter(f => ({ ...f, billing: e.target.value }))}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-800"
        >
          <option value="">All Billing</option>
          <option value="active">Active</option>
          <option value="usage_exceeded">Usage Exceeded</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Clients List */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-gray-800 font-semibold">Clients ({clients.length})</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : (
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50/50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Plan</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Minutes</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Last Call</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Allowed</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {clients.map((client, idx) => {
                    const clientId = client.id || String(client._id);
                    return (
                      <tr 
                        key={clientId || idx} 
                        className={`hover:bg-gray-50/30 cursor-pointer ${selectedClient === clientId ? "bg-gray-50/50" : ""}`}
                        onClick={() => {
                          setSelectedClient(clientId);
                          fetchClientDetail(clientId);
                        }}
                      >
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-gray-800 font-medium">{client.name || "Unnamed"}</p>
                            <p className="text-gray-500 text-xs truncate max-w-[150px]">{client.email || clientId}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-gray-600 text-sm capitalize">
                            {client.plan_id?.replace("_", " ") || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <StatusBadge status={client.onboarding_status} />
                            <StatusBadge status={client.billing_status} type="billing" />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <p className="text-gray-800 font-medium">
                              {Math.round(client.minutes_used || 0)} / {client.minutes_included || 300}
                            </p>
                            <p className="text-gray-400 text-xs">
                              {client.locations_used || 1} loc
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-gray-500 text-xs">
                            {client.last_call_at 
                              ? new Date(client.last_call_at).toLocaleDateString()
                              : "Never"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {client.allowed ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedClient(clientId);
                              fetchClientDetail(clientId);
                            }}
                            className="p-2 hover:bg-gray-200 rounded-lg"
                          >
                            <Eye className="w-4 h-4 text-gray-500" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Client Detail Panel */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          {clientDetail ? (
            <div className="space-y-4">
              <h3 className="text-gray-800 font-semibold text-lg">{clientDetail.client?.name || "Client Details"}</h3>
              
              {/* Status Badges */}
              <div className="flex flex-wrap gap-2">
                <StatusBadge status={clientDetail.client?.onboarding_status} />
                <StatusBadge status={clientDetail.client?.billing_status} type="billing" />
                {clientDetail.admission?.accept ? (
                  <span className="px-2 py-1 rounded-full text-xs font-medium border bg-green-100 text-green-600 border-green-200">
                    Calls: Allowed
                  </span>
                ) : (
                  <span className="px-2 py-1 rounded-full text-xs font-medium border bg-red-100 text-red-600 border-red-200">
                    Calls: Blocked
                  </span>
                )}
              </div>

              {/* Reason for block */}
              {!clientDetail.admission?.accept && (
                <div className="bg-red-900/30 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm"><strong>Blocked:</strong> {clientDetail.admission?.reason}</p>
                </div>
              )}

              {/* Usage */}
              <div className="bg-gray-50/50 rounded-lg p-3">
                <h4 className="text-gray-600 text-sm font-semibold mb-2">Usage This Month</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-gray-500">Minutes Used</p>
                    <p className="text-gray-800 font-bold">{Math.round(clientDetail.billing?.usage?.minutes_used || 0)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Included</p>
                    <p className="text-gray-800 font-bold">{clientDetail.billing?.usage?.minutes_included || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Calls</p>
                    <p className="text-gray-800 font-bold">{clientDetail.billing?.usage?.calls_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Usage %</p>
                    <p className="text-gray-800 font-bold">{(clientDetail.billing?.usage?.usage_percent || 0).toFixed(1)}%</p>
                  </div>
                </div>
              </div>

              {/* Plan */}
              <div className="bg-gray-50/50 rounded-lg p-3">
                <h4 className="text-gray-600 text-sm font-semibold mb-2">Plan</h4>
                <p className="text-gray-800 capitalize">{clientDetail.billing?.plan?.plan_id || "Unknown"}</p>
                <p className="text-gray-500 text-xs">Hard cap: {clientDetail.billing?.hard_cap || "N/A"} min</p>
              </div>

              {/* QA Status Section */}
              <div className={`rounded-lg p-3 border ${
                qaStatus?.qa_status === "passed" ? "bg-green-50 border-green-200" :
                qaStatus?.qa_status === "failed" ? "bg-red-50 border-red-200" :
                "bg-yellow-50 border-yellow-200"
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-gray-700 text-sm font-semibold flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4" />
                    QA Status
                  </h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    qaStatus?.qa_status === "passed" ? "bg-green-100 text-green-700" :
                    qaStatus?.qa_status === "failed" ? "bg-red-100 text-red-700" :
                    "bg-yellow-100 text-yellow-700"
                  }`}>
                    {qaStatus?.qa_status?.toUpperCase() || "PENDING"}
                  </span>
                </div>
                
                {qaStatus?.qa_status === "passed" && qaStatus?.qa_passed_at && (
                  <p className="text-green-700 text-xs">
                    Passed on {new Date(qaStatus.qa_passed_at).toLocaleString()}
                  </p>
                )}
                
                {qaStatus?.qa_status !== "passed" && (
                  <div className="mt-2 p-2 bg-yellow-100 rounded border border-yellow-300">
                    <p className="text-yellow-800 text-xs font-medium">
                      ⚠️ QA must pass before client can be activated
                    </p>
                  </div>
                )}
                
                <button
                  onClick={() => setQaModal(true)}
                  className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#4F80F5] text-white rounded-lg hover:bg-[#3D6FE4] text-sm font-medium"
                >
                  <ClipboardCheck className="w-4 h-4" />
                  {qaStatus?.qa_status === "passed" ? "View QA Checklist" : "Run QA Checklist"}
                </button>
              </div>

              {/* Actions */}
              <div className="space-y-2 pt-4 border-t border-gray-200">
                <h4 className="text-gray-600 text-sm font-semibold">Actions</h4>
                
                {clientDetail.client?.onboarding_status !== "live" && (
                  <div>
                    <button 
                      onClick={() => {
                        if (qaStatus?.qa_status !== "passed") {
                          alert("Cannot activate: QA must pass first. Please complete the QA checklist.");
                          setQaModal(true);
                          return;
                        }
                        setActionModal({ type: "activate", clientId: selectedClient });
                      }}
                      disabled={qaStatus?.qa_status !== "passed"}
                      className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg ${
                        qaStatus?.qa_status === "passed"
                          ? "bg-green-600 text-white hover:bg-green-700"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      <Power className="w-4 h-4" /> 
                      {qaStatus?.qa_status === "passed" ? "Activate Client" : "QA Required First"}
                    </button>
                    {qaStatus?.qa_status !== "passed" && (
                      <p className="text-xs text-yellow-600 mt-1">
                        ⚠️ Complete QA checklist before activation
                      </p>
                    )}
                  </div>
                )}
                
                {clientDetail.client?.onboarding_status !== "suspended" && (
                  <button 
                    onClick={() => setActionModal({ type: "suspend", clientId: selectedClient })}
                    className="w-full flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    <XCircle className="w-4 h-4" /> Suspend Client
                  </button>
                )}
                
                <button 
                  onClick={() => setActionModal({ type: "grant-grace", clientId: selectedClient })}
                  className="w-full flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                >
                  <Clock className="w-4 h-4" /> Grant Grace Period
                </button>
                
                {!clientDetail.client?.setup_fee_paid && (
                  <button 
                    onClick={() => setActionModal({ type: "waive-setup", clientId: selectedClient })}
                    className="w-full flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    <DollarSign className="w-4 h-4" /> Waive Setup Fee
                  </button>
                )}

                <button 
                  onClick={() => {
                    setNewPlan(clientDetail.billing?.plan?.plan_id || "receptionist");
                    setActionModal({ type: "change-plan", clientId: selectedClient });
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <ArrowUpDown className="w-4 h-4" /> Change Plan
                </button>
              </div>

              {/* Recent Calls */}
              {clientDetail.recent_calls?.length > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-gray-600 text-sm font-semibold mb-2">Recent Calls</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {clientDetail.recent_calls.slice(0, 5).map((call, idx) => (
                      <div key={call.id || idx} className="bg-gray-50/50 rounded p-2 text-xs">
                        <p className="text-gray-800">{call.caller_number || "Unknown"}</p>
                        <p className="text-gray-500">{call.duration || 0}s • {new Date(call.created_at).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Select a client to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Action Modal */}
      {actionModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 border border-gray-200">
            <h3 className="text-xl font-bold text-gray-800 mb-4 capitalize">
              {actionModal.type.replace("-", " ")}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-600 text-sm mb-1">Reason (required for audit)</label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder="Enter reason for this action..."
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows={3}
                />
              </div>
              
              {actionModal.type === "grant-grace" && (
                <div>
                  <label className="block text-gray-600 text-sm mb-1">Grace Period (hours)</label>
                  <input
                    type="number"
                    value={graceHours}
                    onChange={(e) => setGraceHours(parseInt(e.target.value) || 72)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800"
                    min={1}
                    max={720}
                  />
                </div>
              )}

              {actionModal.type === "change-plan" && (
                <div>
                  <label className="block text-gray-600 text-sm mb-1">New Plan</label>
                  <select
                    value={newPlan}
                    onChange={(e) => setNewPlan(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800"
                  >
                    <option value="receptionist">The Receptionist ($199/mo - 300 min)</option>
                    <option value="frontdesk">The Front Desk ($349/mo - 800 min)</option>
                    <option value="regional">Regional HQ ($699/mo - 2000 min)</option>
                  </select>
                  <p className="text-gray-500 text-xs mt-2">
                    Note: Downgrades should apply next billing cycle. Upgrades are immediate.
                  </p>
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <button 
                  onClick={() => { setActionModal(null); setActionReason(""); }}
                  className="flex-1 py-2 bg-gray-50 text-gray-800 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => handleAction(actionModal.type, actionModal.clientId)}
                  className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QA Checklist Modal */}
      {qaModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <ClipboardCheck className="w-6 h-6 text-[#4F80F5]" />
                QA Checklist
              </h3>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                qaStatus?.qa_status === "passed" ? "bg-green-100 text-green-700" :
                qaStatus?.qa_status === "failed" ? "bg-red-100 text-red-700" :
                "bg-yellow-100 text-yellow-700"
              }`}>
                {qaStatus?.qa_status?.toUpperCase() || "PENDING"}
              </span>
            </div>

            {/* Warning Banner if not passed */}
            {qaStatus?.qa_status !== "passed" && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-sm font-medium">
                  ⚠️ All items must pass before client can be activated. This is the quality moat.
                </p>
              </div>
            )}

            {/* Checklist Items */}
            <div className="space-y-3 mb-4">
              {qaChecklist.map((item, index) => (
                <div 
                  key={item.item}
                  onClick={() => toggleQaItem(index)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    item.passed 
                      ? "bg-green-50 border-green-200" 
                      : "bg-gray-50 border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {item.passed ? (
                      <CheckSquare className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                    <span className={`text-sm ${item.passed ? "text-green-800" : "text-gray-700"}`}>
                      {item.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Progress */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Progress</span>
                <span className="text-gray-800 font-medium">
                  {qaChecklist.filter(i => i.passed).length}/{qaChecklist.length} items
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 transition-all"
                  style={{ width: `${(qaChecklist.filter(i => i.passed).length / qaChecklist.length) * 100}%` }}
                />
              </div>
            </div>

            {/* PROOF FIELDS (REQUIRED) */}
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="text-blue-800 font-semibold text-sm mb-3 flex items-center gap-2">
                🔐 Proof Artifacts (Required)
              </h4>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    Test Call ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={qaTestCallId}
                    onChange={(e) => setQaTestCallId(e.target.value)}
                    placeholder="Enter the test call ID from Calls table..."
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Must exist in system and match this client</p>
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    Recording URL (optional)
                  </label>
                  <input
                    type="text"
                    value={qaRecordingUrl}
                    onChange={(e) => setQaRecordingUrl(e.target.value)}
                    placeholder="Link to call recording..."
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setQaSummaryReceived(!qaSummaryReceived)}
                    className={`w-6 h-6 rounded border flex items-center justify-center ${
                      qaSummaryReceived ? "bg-green-500 border-green-500" : "bg-white border-gray-300"
                    }`}
                  >
                    {qaSummaryReceived && <CheckSquare className="w-4 h-4 text-white" />}
                  </button>
                  <label className="text-gray-700 text-sm">
                    Summary received in dashboard <span className="text-red-500">*</span>
                  </label>
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    Performed By <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={qaPerformedBy}
                    onChange={(e) => setQaPerformedBy(e.target.value)}
                    placeholder="Your name (for audit trail)..."
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Reason Input */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Notes / Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={qaReason}
                onChange={(e) => setQaReason(e.target.value)}
                placeholder="Enter QA notes or reason for pass/fail..."
                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4F80F5]"
                rows={2}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button 
                onClick={() => { setQaModal(false); resetQaForm(); }}
                className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
              >
                Cancel
              </button>
              <button 
                onClick={handleQaFail}
                disabled={!qaReason.trim()}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
              >
                Mark Failed
              </button>
              <button 
                onClick={handleQaPass}
                disabled={!qaReason.trim() || !qaChecklist.every(i => i.passed) || !qaTestCallId.trim() || !qaSummaryReceived || !qaPerformedBy.trim()}
                className="flex-1 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
              >
                Mark Passed
              </button>
            </div>

            {/* Already Passed Info */}
            {qaStatus?.qa_status === "passed" && qaStatus?.qa_passed_at && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                <p className="text-green-800">
                  <strong>QA Passed</strong> on {new Date(qaStatus.qa_passed_at).toLocaleString()}
                </p>
                {qaStatus.qa_passed_by && (
                  <p className="text-green-700">by {qaStatus.qa_passed_by}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status, type }) {
  const getStyle = () => {
    if (type === "billing") {
      switch (status) {
        case "active": return "bg-green-100 text-green-600 border-green-200";
        case "usage_exceeded": return "bg-red-100 text-red-600 border-red-200";
        case "hard_cap_reached": return "bg-red-100 text-red-600 border-red-200";
        case "suspended": return "bg-red-100 text-red-600 border-red-200";
        case "grace_period": return "bg-yellow-100 text-yellow-700 border-yellow-200";
        default: return "bg-gray-50 text-gray-600 border-gray-300";
      }
    }
    switch (status) {
      case "live": return "bg-green-100 text-green-600 border-green-200";
      case "in_review": return "bg-blue-100 text-blue-600 border-blue-200";
      case "pending_setup": return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "suspended": return "bg-red-100 text-red-600 border-red-200";
      default: return "bg-gray-50 text-gray-600 border-gray-300";
    }
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStyle()}`}>
      {status || "unknown"}
    </span>
  );
}

export default AdminClientsPage;
